import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Progress as ProgressBar } from 'antd';
import { graphql, compose } from 'react-apollo';
import queries from 'graph/queries';
import _ from 'lodash';

import Panel from 'common/Panel';
import ReportPanel from 'common/ReportPanel';
import TitlePanel from 'common/TitlePanel';
import {
  STRATUM,
  getHealthIndex,
  getTotalScoreOverTime,
} from 'utils/progress';
import HealthScore from './HealthScore';
import ScoreOverTime from './ScoreOverTime';
import HealthIndexes from './HealthIndexes';

const formatDisplayValue = scoreInPercentage => _.round((scoreInPercentage / 100) * 10, 1);

class Progress extends Component {
  static propTypes = {
    measureReports: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      patientID: PropTypes.string,
      identifier: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string,
      })),
      status: PropTypes.string,
      type: PropTypes.string,
      startedAt: PropTypes.string,
      endedAt: PropTypes.string,
      improvementNotation: PropTypes.string,
      groups: PropTypes.arrayOf(PropTypes.shape({
        stratifier: PropTypes.arrayOf(
          PropTypes.shape({
            stratum: PropTypes.arrayOf(
              PropTypes.shape({
                value: PropTypes.string,
                measureScore: PropTypes.number,
              }),
            ),
          }),
        ),
      })),
    })).isRequired,
  };

  getScoreWithNulls = (identifier, formatValuesToDisplay) => {
    const { measureReports } = this.props;
    const nullIdentifiers = _.without(['readiness', 'confidence', 'total_health_index'], identifier);

    const scores = _.reduce([identifier, ...nullIdentifiers], (allScores, identifierName) => {
      const isIdentifierMatching = identifier !== identifierName;
      return (
        [
          ...allScores,
          ...getTotalScoreOverTime(measureReports, identifierName, isIdentifierMatching, formatValuesToDisplay),
        ]
      );
    }, []);

    return _.orderBy(scores, ['xLabel']);
  }

  render() {
    const { measureReports } = this.props;
    const totalScore = getHealthIndex(measureReports, 'total_health_index');
    const readiness = getHealthIndex(measureReports, 'readiness');
    const confidence = getHealthIndex(measureReports, 'confidence');

    const data = STRATUM.map(index => ({
      label: index.label,
      Score: getHealthIndex(measureReports, index.key),
    }));

    const sortedData = [
      {
        name: 'Readiness Score',
        stroke: '#82ca9d',
        data: this.getScoreWithNulls('readiness', formatDisplayValue),
      },
      {
        name: 'Confidence Score',
        stroke: '#FF6F02',
        data: this.getScoreWithNulls('confidence', formatDisplayValue),
      },
      {
        name: 'Health Score',
        stroke: '#D0021B',
        data: this.getScoreWithNulls('total_health_index'),
      },
    ];

    return (
      <Fragment>
        <TitlePanel className="pa3 flex items-center justify-center primary">
          <h2>Check out your SHI score</h2>
        </TitlePanel>
        <Panel id="scene-careplan" className="pa3-ns pa0">
          <div className="flex-l">
            <div className="w-50-l w-100 mr3">
              <ReportPanel title="Total Health Score" className="bb">
                <HealthScore score={totalScore} />
              </ReportPanel>
              <div className="flex-m">
                <ReportPanel className="mt3 mr3-m w-50-m pb3-lg pb3-m pb4 bb" title="Overall Confidence">
                  <ProgressBar percent={confidence} strokeColor="#375784" />
                </ReportPanel>
                <ReportPanel className="mt3 w-50-m pb0-lg pb0-m pb4 bb" title="Overall Readiness">
                  <ProgressBar percent={readiness} strokeColor="#375784" />
                </ReportPanel>
              </div>
            </div>
            <div className="w-100 w-50-l mt3 mt0-l">
              <ReportPanel className="h-100 bb" title="Health Insight Map">
                <HealthIndexes data={data} />
              </ReportPanel>
            </div>
          </div>
          <ReportPanel className="dib w-100 mt3" title="Total Score Over Time">
            <ScoreOverTime data={sortedData} />
          </ReportPanel>
        </Panel>
      </Fragment>
    );
  }
}

export default compose(
  graphql(queries.measureReports, {
    options: () => ({
      fetchPolicy: 'cache-and-network',
      // variables: { carePlanId }
    }),
    props: ({ data: { loading, measureReports } }) => ({
      measureReports,
      loading,
    }),
  }),
)(Progress);
